import type { ReactNode } from "react";
import { NavLink } from "react-router";

interface ButtonProps {
  type?: "circle" | "regular";
  style?: "success" | "error" | "primary" | "accent" | "secondary" | "warning" | "multi-colored";
  size?: "small" | "regular" | "large";
  mode: "button" | "link" | "navLink";
  onClick?: () => void;
  className?: string;
  href?: string;
  children?: ReactNode;
  shadowEffects?: boolean;
  tooltipText?: unknown;
  disabled?: boolean;
  analyticsTrackingName?: string;
}

export default function Button({
  type = "regular",
  style = "secondary",
  size = "regular",
  mode,
  onClick,
  href,
  className = "",
  children,
  shadowEffects = true,
  tooltipText,
  disabled = false,
  analyticsTrackingName,
}: ButtonProps) {
  const buttonClassNameItems = ["btn"];

  if (size !== "regular") {
    if (size === "small") {
      buttonClassNameItems.push("btn-sm");
    } else {
      buttonClassNameItems.push("btn-lg");
    }
  }

  if (shadowEffects) {
    buttonClassNameItems.push("shadow-sm", "hover:shadow-md");
  }

  if (type !== "regular") {
    buttonClassNameItems.push("btn-circle");
  }

  const buttonClassName = buttonClassNameItems.join(" ");

  if (mode === "button") {
    if (!onClick) {
      throw new Error("Mode is button but is missing onClick prop");
    }

    const buttonBlock = analyticsTrackingName ? (
      <button
        type="button"
        data-track={analyticsTrackingName}
        disabled={disabled}
        className={`${buttonClassName} btn-${style} shadow-sm hover:shadow-md ${className}`}
        onClick={onClick}
      >
        <div className="z-10 flex justify-around items-center">{children}</div>
      </button>
    ) : (
      <button
        type="button"
        disabled={disabled}
        className={`${buttonClassName} btn-${style} shadow-sm hover:shadow-md ${className}`}
        onClick={onClick}
      >
        <div className="z-10 flex justify-around items-center">{children}</div>
      </button>
    );

    if (tooltipText) {
      return (
        <div className="tooltip" data-tip={tooltipText as string}>
          {buttonBlock}
        </div>
      );
    }

    return buttonBlock;
  } else if (mode === "navLink") {
    if (!href) {
      throw new Error("Mode is navLink but is missing href prop");
    }

    const navLinkBlock = analyticsTrackingName ? (
      <NavLink
        data-track={analyticsTrackingName}
        unselectable={disabled ? "on" : "off"}
        className={`${buttonClassName} btn-${style} shadow-sm hover:shadow-md ${className}`}
        to={href}
      >
        <div className="z-10 flex justify-around items-center">{children}</div>
      </NavLink>
    ) : (
      <NavLink
        unselectable={disabled ? "on" : "off"}
        className={`${buttonClassName} btn-${style} shadow-sm hover:shadow-md ${className}`}
        to={href}
      >
        <div className="z-10 flex justify-around items-center">{children}</div>
      </NavLink>
    );

    if (tooltipText) {
      return (
        <div className="tooltip" data-tip={tooltipText as string}>
          {navLinkBlock}
        </div>
      );
    }

    return navLinkBlock;
  }

  if (!href) {
    throw new Error("Mode is link but is missing href prop");
  }

  const aBlock = analyticsTrackingName ? (
    <a
      data-track={analyticsTrackingName}
      aria-disabled={disabled}
      className={`${buttonClassName} btn-${style} shadow-sm hover:shadow-md ${className}`}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      <div className="z-10 flex justify-around items-center">{children}</div>
    </a>
  ) : (
    <a
      aria-disabled={disabled}
      className={`${buttonClassName} btn-${style} shadow-sm hover:shadow-md ${className}`}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      <div className="z-10 flex justify-around items-center">{children}</div>
    </a>
  );

  if (tooltipText) {
    return (
      <div className="tooltip" data-tip={tooltipText as string}>
        {aBlock}
      </div>
    );
  }

  return aBlock;
}
